import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'

const SERVICES = [
  {
    num: '01',
    title: 'Websites for Small Businesses',
    short: 'Custom sites that actually bring in customers.',
    detail: "We build proper websites for small businesses — not drag-and-drop templates, not Wix. Landing pages, service sites, booking pages, and portfolio sites built with React or Next.js and Tailwind. Fast, mobile-first, and SEO-ready from day one. You get something that looks professional and actually converts visitors.",
    tags: ['React', 'Next.js', 'Tailwind', 'SEO', 'Mobile-First'],
    metric: 'No templates, ever',
  },
  {
    num: '02',
    title: 'Stripe & Payment Integrations',
    short: 'Get paid online — properly wired up.',
    detail: "We handle the full Stripe integration: checkout flows, one-time payments, subscriptions, webhooks, and customer portals.",
    tags: ['Stripe', 'Webhooks', 'Subscriptions', 'Payments'],
    metric: 'End-to-end payment setup',
  },
  {
    num: '03',
    title: 'Web Apps & Dashboards',
    short: 'Custom tools built around how you actually work.',
    detail: "Need something more than a brochure site? We build web apps — booking systems, client portals, internal dashboards, SaaS MVPs, and admin panels. If you can describe the problem, we can build the tool. We use React, Node.js, and PostgreSQL or Supabase depending on what fits best.",
    tags: ['React', 'Node.js', 'Supabase', 'Dashboards', 'Booking Systems'],
    metric: 'Custom-built for your workflow',
  },
]

function ServiceRow({ svc, index }) {
  const [open, setOpen] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', border: 'none', background: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '28px 0', gap: 20, textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: 1, minWidth: 0 }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
            color: open ? '#22d3ee' : 'rgba(255,255,255,0.5)',
            letterSpacing: '.12em', flexShrink: 0,
            transition: 'color 0.25s',
            minWidth: 24,
          }}>
            {svc.num}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 style={{
              fontFamily: 'Geist Variable, sans-serif',
              fontSize: 'clamp(17px, 2.2vw, 24px)', fontWeight: 600,
              color: open ? '#fff' : 'rgba(255,255,255,0.78)',
              letterSpacing: '-0.025em',
              transition: 'color 0.25s',
              lineHeight: 1.2,
            }}>
              {svc.title}
            </h3>
            {!open && (
              <p style={{
                fontSize: 13, color: 'rgba(255,255,255,0.5)',
                marginTop: 4, lineHeight: 1.5,
              }}>
                {svc.short}
              </p>
            )}
          </div>
        </div>

        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          border: `1px solid ${open ? 'rgba(6,182,212,0.4)' : 'rgba(255,255,255,0.1)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? '#22d3ee' : 'rgba(255,255,255,0.5)',
          transition: 'border-color 0.25s, color 0.25s, transform 0.3s cubic-bezier(.22,1,.36,1)',
          transform: open ? 'rotate(45deg)' : 'none',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              paddingBottom: 32,
              paddingLeft: 28,
              borderLeft: '2px solid rgba(6,182,212,0.15)',
              marginLeft: 5,
              marginBottom: 4,
            }}>
              <p style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: 14, lineHeight: 1.85,
                marginBottom: 20, maxWidth: 540,
              }}>
                {svc.detail}
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 24 }}>
                {svc.tags.map((t) => (
                  <span key={t} style={{
                    padding: '3px 11px', borderRadius: 6,
                    fontSize: 11, fontFamily: 'monospace',
                    color: '#22d3ee',
                    background: 'rgba(6,182,212,0.07)',
                    border: '1px solid rgba(6,182,212,0.16)',
                    letterSpacing: '0.04em',
                  }}>
                    {t}
                  </span>
                ))}
              </div>

              <Link to="/contact" className="btn-primary" style={{
                display: 'inline-flex', fontSize: 13,
                padding: '10px 22px',
              }}>
                <span>Get a quote</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{
        height: 1,
        background: open ? 'rgba(6,182,212,0.1)' : 'rgba(255,255,255,0.05)',
        transition: 'background 0.25s',
      }} />
    </motion.div>
  )
}

export default function Services() {
  return (
    <section id="services" style={{
      padding: '120px 0', background: '#000',
      position: 'relative', overflow: 'hidden',
    }}>
      <div className="bg-orb" style={{
        width: 480, height: 260,
        background: 'rgba(6,182,212,0.05)',
        top: '30%', right: '-6%',
        filter: 'blur(90px)',
      }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 2 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: 80, alignItems: 'start',
        }} className="svc-layout">

          {/* Left sticky label */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: 'sticky', top: 120 }}
            className="svc-sticky"
          >
            <h2 className="display-lg heading-glow" style={{ color: '#fff', lineHeight: 1.0 }}>
              What we<br />
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>build.</span>
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.5)', fontSize: 14,
              lineHeight: 1.8, marginTop: 18, maxWidth: 220,
            }}>
              Websites, web apps, and payment integrations — built properly, from scratch, around what your business actually needs.
            </p>

            <div style={{ marginTop: 40 }}>
              <Link to="/contact" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 13, color: '#22d3ee', textDecoration: 'none',
                fontWeight: 500, transition: 'gap 0.2s',
                padding: '6px 0',
              }}
                onMouseEnter={e => { e.currentTarget.style.gap = '14px' }}
                onMouseLeave={e => { e.currentTarget.style.gap = '8px' }}
              >
                Start a project
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </motion.div>

          {/* Right — accordion list */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            {SERVICES.map((svc, i) => (
              <ServiceRow key={svc.num} svc={svc} index={i} />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media(max-width: 768px) {
          .svc-layout { grid-template-columns: 1fr !important; gap: 40px !important; }
          /* position:sticky only makes sense in the two-column desktop layout —
             stacked single-column on mobile, it would pin itself over the
             accordion items below as the page scrolls. */
          .svc-sticky { position: static !important; }
        }
      `}</style>
    </section>
  )
}
